import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { AssetIcon } from '@Components/shared/images/AssetIcon';
import { Button, Popover, Stack, Typography, useTheme } from '@mui/material';
import { TextField } from '@mui/material';

type Props = {
  title: string;
  onApply: (startDate?: string | null, endDate?: string | null) => void;
  onReset: () => void;
  includeTime?: boolean;
  maxDaysRange?: number;
  defaultStartDate?: string;
  defaultEndDate?: string;
};

export const DateTableColumnHeader: React.FC<Props> = ({
  title,
  onApply,
  onReset,
  includeTime = false,
  maxDaysRange,
  defaultStartDate,
  defaultEndDate,
}) => {
  // Helper function to format ISO date to input format
  const formatISOToInputFormat = useCallback(
    (isoDate?: string) => {
      if (!isoDate) return '';
      const date = new Date(isoDate);
      return includeTime
        ? date.toISOString().slice(0, 16) // Format: YYYY-MM-DDTHH:mm
        : date.toISOString().slice(0, 10); // Format: YYYY-MM-DD
    },
    [includeTime]
  );

  const [open, setOpen] = useState(false);
  const [startDate, setStartDate] = useState<string | null>(
    formatISOToInputFormat(defaultStartDate)
  );
  const [endDate, setEndDate] = useState<string | null>(formatISOToInputFormat(defaultEndDate));
  const anchorRef = useRef<HTMLDivElement>(null);
  const { palette } = useTheme();

  // Calculate min and max dates if maxDaysRange is provided
  const getMinDate = useCallback(() => {
    if (!maxDaysRange) return undefined;
    const today = new Date();
    const minDate = new Date(today);
    minDate.setDate(today.getDate() - maxDaysRange);
    return includeTime
      ? minDate.toISOString().slice(0, 16) // Format: YYYY-MM-DDTHH:mm
      : minDate.toISOString().slice(0, 10); // Format: YYYY-MM-DD
  }, [maxDaysRange, includeTime]);

  const getMaxDate = useCallback(() => {
    const today = new Date();
    return includeTime
      ? today.toISOString().slice(0, 16) // Format: YYYY-MM-DDTHH:mm
      : today.toISOString().slice(0, 10); // Format: YYYY-MM-DD
  }, [includeTime]);

  const minDate = getMinDate();
  const maxDate = getMaxDate();

  useEffect(() => {
    setStartDate(formatISOToInputFormat(defaultStartDate));
    setEndDate(formatISOToInputFormat(defaultEndDate));
  }, [defaultStartDate, defaultEndDate, formatISOToInputFormat]);

  const handleStartDateChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      setStartDate(event.target.value || '');
    },
    [setStartDate]
  );

  const handleEndDateChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      setEndDate(event.target.value || '');
    },
    [setEndDate]
  );

  const handleClick = useCallback(() => {
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleApplyClick = useCallback(() => {
    let convertedStartDate: string | undefined = undefined;
    let convertedEndDate: string | undefined = undefined;

    if (startDate) {
      if (includeTime) {
        // If datetime-local format, convert directly
        convertedStartDate = new Date(startDate).toISOString();
      } else {
        // If date only, add 00:00:00 time
        const startDateTime = new Date(startDate);
        startDateTime.setHours(0, 0, 0, 0);
        convertedStartDate = startDateTime.toISOString();
      }
    }

    if (endDate) {
      if (includeTime) {
        // If datetime-local format, convert directly
        convertedEndDate = new Date(endDate).toISOString();
      } else {
        // If date only, add 23:59:59 time
        const endDateTime = new Date(endDate);
        endDateTime.setHours(23, 59, 59, 999);
        convertedEndDate = endDateTime.toISOString();
      }
    }

    onApply(convertedStartDate, convertedEndDate);
  }, [onApply, startDate, endDate, includeTime]);

  const handleResetClick = useCallback(() => {
    setStartDate(formatISOToInputFormat(defaultStartDate));
    setEndDate(formatISOToInputFormat(defaultEndDate));

    onReset && onReset();
  }, [onReset, formatISOToInputFormat, defaultStartDate, defaultEndDate]);

  return (
    <>
      <Stack
        ref={anchorRef}
        sx={{
          backgroundColor: 'linear-gradient(to bottom, #F4F6F8 0%, #F4F6F8 100%)',
        }}
      >
        <Button
          color="inherit"
          endIcon={<AssetIcon src="search-lg" size="m" sx={{ bgcolor: palette.primary.main }} />}
          onClick={handleClick}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            '&:hover': { backgroundColor: 'inherit' },
          }}
        >
          <Typography
            sx={{ fontSize: '14px', lineHeight: '24px', color: '#637381', fontWeight: '600' }}
          >
            {title ? title : ''}
          </Typography>
        </Button>
      </Stack>
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        sx={{ marginTop: 1 }}
      >
        <Stack sx={{ width: '209px', height: 'auto', padding: '8px', gap: '8px' }}>
          <TextField
            size="small"
            label={includeTime ? 'Start Date & Time' : 'Start Date'}
            type={includeTime ? 'datetime-local' : 'date'}
            data-at-text-field="StartDateFilter"
            value={startDate}
            onChange={handleStartDateChange}
            fullWidth
            InputLabelProps={{ shrink: true }}
            inputProps={{
              min: minDate,
              max: maxDate,
            }}
          />
          <TextField
            size="small"
            label={includeTime ? 'End Date & Time' : 'End Date'}
            type={includeTime ? 'datetime-local' : 'date'}
            data-at-text-field="EndDateFilter"
            value={endDate}
            onChange={handleEndDateChange}
            fullWidth
            InputLabelProps={{ shrink: true }}
            inputProps={{
              min: minDate,
              max: maxDate,
            }}
          />
          <Stack sx={{ flexDirection: 'row', justifyContent: 'center', marginTop: '8px' }}>
            <Button
              size="small"
              variant="contained"
              data-at-button="Apply"
              onClick={handleApplyClick}
            >
              Apply
            </Button>
            <Button
              size="small"
              variant="text"
              color="inherit"
              data-at-button="Reset"
              onClick={handleResetClick}
            >
              Reset
            </Button>
          </Stack>
        </Stack>
      </Popover>
    </>
  );
};
